import { Injectable } from '@angular/core';
import { HttpEvent, HttpClient, HttpErrorResponse, HttpEventType, HttpResponse } from '@angular/common/http';
import { ConfigService } from '../config.service';
import { catchError, filter, map } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { Utilities } from 'src/app/_helpers/utility/utilities';
import { EInterceptorOptionsHTTPHeaders } from 'src/app/_helpers/interceptors/EInterceptorOptionsHTTPHeaders';

type DownloadKey = 'analysis' | 'decisions' | 'incidentInfo' | 'otherIncidentInfo';

@Injectable({
  providedIn: 'root'
})
export class LegacyIncidentDownloadService {

  private baseURL = this.environment.commonUtilityApi.baseUrl;

  private endpoints: Record<DownloadKey, (legacyId: number) => string> = {
    analysis: (legacyId) => `/s3legacy/${legacyId}/analysisZip`,
    decisions: (legacyId) => `/s3legacy/${legacyId}/decisionsZip`,
    incidentInfo: (legacyId) => `/s3legacy/${legacyId}/infoReportPdf`,
    otherIncidentInfo: (legacyId) => `/s3legacy/${legacyId}/otherIncidentInfoZip`
  };

  constructor(private http: HttpClient, private environment: ConfigService) { }

  downloadFile(
    key: DownloadKey, 
    wfdssId: number,
    incidentName: string,
    uniqueFireIdentifier: string): Observable<{  progress: number; totalSize: number; completed: boolean }> {

    const endpoint = this.endpoints[key](wfdssId);

    return this.http.get(this.baseURL + endpoint, { 
      observe: 'events',
      responseType: 'blob',
      reportProgress: true,
      headers: Utilities.appendHTTPHeaderForInterceptor([
                EInterceptorOptionsHTTPHeaders.SuppressErrorInterceptor,
              ])
    }).pipe(
      map((event: HttpEvent<Blob>) : { progress: number; totalSize: number; completed: boolean } | undefined =>  {
        if (event.type === HttpEventType.ResponseHeader) {
          const totalSize = Number(event.headers?.get('Content-Length') || 0);
          return { progress: 0, totalSize, completed: false };  
        }

        if (event.type === HttpEventType.DownloadProgress) {
          const total = event.total  ?? 0;
          const progress = total > 0 ? Math.round((event.loaded / total) * 100) : 0;
          return { progress, totalSize: event.total, completed: false };
        }

        if (event.type === HttpEventType.Response) {
          const totalSize = Number(event.headers.get('Content-Length') || 0);
          const filename = this.buildfilename(incidentName, uniqueFireIdentifier, key);
          this.saveFile(event.body!, filename);
          return { progress: 100, totalSize, completed: true };
        }

        return undefined;
      }),

     catchError((error: any) => {
          return new Observable<any>(observer => {
            if (error?.status === 404) {
              observer.error(new Error('No files of this type were found for this incident. Please contact support staff if you feel this is incorrect.'));
            } else {    
             observer.error(new Error('Download failed. Please try again later or contact support staff if the issue persists.'));
          }   
       });
      }),

      filter((value): value is { progress: number; totalSize: number; completed: boolean } => value !== undefined)

     
    );
  }

  private buildfilename(incidentName: string, uniqueFireIdentifier: string, key: DownloadKey): string {
    const sanitizedIncidentName = incidentName?.replace(/[^a-zA-Z0-9]/gi, '_')?.replace(/_+/g, '_')?.trim();
    const sanitizedUniqueFireIdentifier = uniqueFireIdentifier?.replace(/[^a-zA-Z0-9\-]/gi, '_')?.replace(/_+/g, '_')?.trim();
    const suffixMap: Record<DownloadKey, string> = {
      analysis: 'Analysis_Data.zip',
      decisions: 'Decisions.zip',
      incidentInfo: 'Incident_Info.pdf',
      otherIncidentInfo: 'Other_Incident_Info.zip'
    };

    return `${sanitizedIncidentName}_${sanitizedUniqueFireIdentifier}_${suffixMap[key]}`;
  }

  private saveFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  }
}